// packages
import * as React from 'react'
import { ColumnDef, RowSelectionState, SortingState, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, OnChangeFn } from '@tanstack/react-table'
import { ChevronRight, ChevronLeft, Search, SearchX } from 'lucide-react'

// components
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'

// contexts
import { DataTableContext } from '@/contexts/DataTableContext'

// interfaces
interface FilterKeyProps {
  title: string
  value: string | string[]
}

// types
type TableProps<T> = {
  data: T[]
  filterKey?: FilterKeyProps
  columns: ColumnDef<T>[]
  children?: Readonly<React.ReactNode>
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  loading?: boolean
  className?: string
  tableClassName?: string
  hideFilterInput?: boolean
  count?: number
  amount?: number
  onRefetch?: (page: number, search: string) => void
}

//RECEBER onRefetch SOMENTE SE A API CHAMAR DE 10 EM 10

export function CustomDataTable<T>({ data, filterKey, columns, children, className, tableClassName, rowSelection = {}, onRowSelectionChange = () => {}, loading = false, hideFilterInput = false, amount, count, onRefetch }: TableProps<T>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = React.useState('')
  const useDataTable = React.useContext(DataTableContext)
  const searchValue = useDataTable?.search ?? globalFilter
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      rowSelection
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange,
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = String(filterValue ?? '')
        .toLowerCase()
        .trim()
      if (!search) return true

      if (filterKey?.value) {
        const keys = Array.isArray(filterKey.value) ? filterKey.value : [filterKey.value]

        return keys.some(key => {
          const value = row.getValue(key)
          return String(value ?? '')
            .toLowerCase()
            .includes(search)
        })
      }

      return row.getVisibleCells().some(cell => {
        const cellValue = cell.getValue()
        return String(cellValue ?? '')
          .toLowerCase()
          .includes(search)
      })
    }
  })

  const rows = table.getRowModel().rows
  const emptyRowsCount = rows.length < 10 ? 10 - rows.length : 0

  function _handlePageChange(cb: () => void, cbContext: () => void) {
    cbContext()
    cb()
  }

  React.useEffect(() => {
    if (onRefetch) onRefetch(useDataTable?.page, useDataTable?.search)
  }, [useDataTable?.page])

  return (
    <div className={`w-full ${className ?? ''}`}>
      <div className={`w-full flex smAndDown:flex-wrap items-end xs:justify-center smAndUp:${children ? 'justify-between' : 'justify-end'} gap-x-2 gap-y-4 py-3 justify-end`}>
        {children}
        {!hideFilterInput && (
          <div className="relative group smAndDown:w-full mdAndUp:min-w-[360px] flex flex-row gap-2">
            <Input
              placeholder="Filtrar por nome..."
              value={searchValue}
              onKeyDown={e => e.key === 'Enter' && onRefetch && onRefetch(useDataTable?.page, searchValue)}
              onChange={event => {
                const value = event.target.value
                useDataTable?.setSearch?.(value)
                table.setGlobalFilter(value)
              }}
              className="smAndDown:w-full mdAndUp:min-w-[300px] placeholder:text-sm group-focus-within:placeholder:text-gray-600 placeholder:text-gray-500 px-5 dark:bg-primary bg-[#f8f9fa] border-gray-300 text-black "
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button disabled={loading} size="default" variant="default" className="rounded-lg" onClick={() => onRefetch && onRefetch(useDataTable?.page, searchValue)}>
                  <Search className="size-5" />
                </Button>
              </TooltipTrigger>

              <TooltipContent side="top" className="bg-primary text-white text-xs">
                Filtrar Usuários por Nome
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>

      <div className="rounded-md border border-border overflow-hidden bg-card">
        <Table className={tableClassName}>
          <TableHeader className="bg-[#E2E4E7]">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <TableHead key={header.id} className="text-start text-nowrap truncate text-foreground">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="bg-background dark:bg-gray-700">
            {/* border-x border-b rounded-b-lg */}
            {loading ? (
              Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {columns.map((column, colIndex) => (
                    <TableCell key={`skeleton-${index}-${colIndex}`}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : rows.length ? (
              <>
                {rows.map(row => (
                  <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="dark:border-gray-500">
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="text-start">
                        {/* text-nowrap line-clamp-2 */}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {Array.from({ length: emptyRowsCount }).map((_, index) => (
                  <TableRow key={`empty-${index}`} className="dark:border-gray-500">
                    {columns.map((column, colIndex) => (
                      <TableCell key={`empty-${index}-${colIndex}`} className="text-start">
                        &nbsp;
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24">
                  <div className="flex flex-col w-full text-foreground items-center justify-center text-center text-muted-foreground gap-y-2">
                    <SearchX className="size-5" />
                    Sem resultados.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className="flex items-center justify-end gap-2 py-3 px-4 bg-muted/50 border-t border-border smAndDown:flex-col smAndDown:items-stretch">
          <div className="flex-1 text-sm text-muted-foreground smAndDown:text-center">Total de {amount} registros</div>
          <div className="flex gap-x-2 smAndDown:justify-end">
            <Button variant="default" size="sm" className="flex gap-x-2" onClick={() => _handlePageChange(table.previousPage, useDataTable?.decrementPage)} disabled={loading || (!table.getCanPreviousPage() && (!count || useDataTable?.page == 0))}>
              <ChevronLeft className="size-4" />
              <span className="xxsAndDown:hidden">Anterior</span>
            </Button>
            <Button variant="default" size="sm" className="flex gap-x-2" onClick={() => _handlePageChange(table.nextPage, useDataTable?.incrementPage)} disabled={loading || Number(count) < 10 || useDataTable.disableNextButton || (!onRefetch && !table.getCanNextPage())}>
              <span className="xxsAndDown:hidden">Próxima</span>
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
